import { useEffect } from 'react';
import { Actions, Async, F0, FSpread } from '../../helpers/types';
import { muiForm } from '@client/new/helpers/forms';
import { FieldErrors, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { subPriceSchema, priceMixpanelEvents } from './helpers';
import toast from 'react-hot-toast';
import { useExclusiveModel } from './exclusive.model';
import { mixTrack } from '@services/mixpanel';

export type UseExclusiveViewModelParams = {
  influencerId?: string;
};

export type PayloadMap = {
  setSubscriptionPrice: { type: 'annual' | 'monthly'; price?: string };
  enableSubscriptionPrice: 'annual' | 'monthly';
  saveExclusive: F0;
  onHelpClick: string;
};

export type ExclusiveVMActions = Actions<PayloadMap>;
export type ExclusiveVMDispatcher = FSpread<PayloadMap>;

export type SubPriceFormValues = {
  subscriptionAnnualPrice?: string;
  subscriptionMonthlyPrice?: string;
  monthlySelected: boolean;
  annualSelected: boolean;
};

export type ExclusiveViewModel = {
  price: {
    monthlyEnabled: boolean;
    annualEnabled: boolean;
    monthly?: string;
    annual?: string;
    monthlyInitialized: boolean;
    annualInitialized: boolean;
  };
  errors: FieldErrors<SubPriceFormValues>;
  unsavedChanges: boolean;
  readonly: boolean;
  showSubscriptionSettings: boolean;
  submitting: boolean;
} & { dispatch: ExclusiveVMDispatcher };

const formDefaultValues = {
  subscriptionAnnualPrice: undefined,
  subscriptionMonthlyPrice: undefined,
  monthlySelected: false,
  annualSelected: false,
};

export const useExclusiveViewModel = (
  p: UseExclusiveViewModelParams,
): Async<ExclusiveViewModel> => {
  const asyncModel = useExclusiveModel(p);

  const {
    reset,
    clearErrors,
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty, dirtyFields, errors },
  } = muiForm(
    useForm<SubPriceFormValues>({
      defaultValues: formDefaultValues,
      resolver: zodResolver(subPriceSchema),
    }),
  );

  useEffect(() => {
    if (asyncModel.type === 'fetched' && asyncModel.value.creator) {
      const influencer = asyncModel.value.creator;

      reset({
        subscriptionAnnualPrice: influencer?.subscriptionAnnualPrice
          ? (influencer.subscriptionAnnualPrice / 100).toFixed(2)
          : undefined,
        subscriptionMonthlyPrice: influencer?.subscriptionMonthlyPrice
          ? (influencer.subscriptionMonthlyPrice / 100).toFixed(2)
          : undefined,
        monthlySelected: !!influencer?.subscriptionMonthlyPrice,
        annualSelected: !!influencer?.subscriptionAnnualPrice,
      });
    }
  }, [asyncModel.type]);

  if (asyncModel.type !== 'fetched') return asyncModel;

  const model = asyncModel.value;
  const { creator, submitting } = model;

  const monthlySubPrice = watch('subscriptionMonthlyPrice');
  const annualSubPrice = watch('subscriptionAnnualPrice');
  const monthlyPriceEnabled = watch('monthlySelected');
  const annualPriceEnabled = watch('annualSelected');

  const dispatch: ExclusiveVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as ExclusiveVMActions;
    switch (action.type) {
      case 'enableSubscriptionPrice':
        clearErrors();
        if (action.payload === 'monthly') {
          setValue('monthlySelected', !monthlyPriceEnabled, {
            shouldDirty: true,
          });
          setValue('subscriptionMonthlyPrice', undefined, {
            shouldDirty: true,
          });
        }
        if (action.payload === 'annual') {
          setValue('annualSelected', !annualPriceEnabled, {
            shouldDirty: true,
          });
          setValue('subscriptionAnnualPrice', undefined, {
            shouldDirty: true,
          });
        }
        return null;
      case 'setSubscriptionPrice':
        clearErrors();
        if (action.payload.type === 'monthly') {
          setValue('subscriptionMonthlyPrice', action.payload.price, {
            shouldDirty: true,
          });
        }
        if (action.payload.type === 'annual') {
          setValue('subscriptionAnnualPrice', action.payload.price, {
            shouldDirty: true,
          });
        }
        return null;
      case 'saveExclusive':
        saveExclusive(action.payload)();
        return null;
      case 'onHelpClick':
        mixTrack({
          type: 'Exclusive Sub Help Viewed',
          payload: { Source: action.payload },
        });
        return null;
    }
  };

  const saveExclusive = (callback?: () => void) =>
    handleSubmit(async (values) => {
      if (!isDirty) {
        toast.success('Subscription price has been saved');
        typeof callback === 'function' && callback();
        return;
      }
      if (!creator?.id) return;
      priceMixpanelEvents(dirtyFields, values);
      model.saveExclusive({
        monthly: values.monthlySelected
          ? values.subscriptionMonthlyPrice
          : undefined,
        annual: values.annualSelected
          ? values.subscriptionAnnualPrice
          : undefined,
        callback,
      });
      reset(values, {
        keepValues: true,
        keepDirty: false,
        keepDirtyValues: false,
      });
    });

  const exclusiveSettingsReadonly = creator.isExclusive && creator.isLive;

  return {
    type: 'fetched',
    value: {
      dispatch,
      price: {
        monthlyEnabled: monthlyPriceEnabled,
        annualEnabled: annualPriceEnabled,
        monthly: monthlySubPrice,
        annual: annualSubPrice,
        monthlyInitialized: !!creator?.subscriptionMonthlyPrice,
        annualInitialized: !!creator?.subscriptionAnnualPrice,
      },
      errors: errors,
      unsavedChanges: isDirty,
      readonly: exclusiveSettingsReadonly,
      showSubscriptionSettings: creator.isExclusive,
      submitting,
    },
  };
};
